import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { FaPlus, FaEdit, FaTrash, FaTimes } from 'react-icons/fa';

const emptyForm = {
  username: '',
  email: '',
  password: '',
  role: 'editor',
  is_active: true,
};

export default function AdminUsers() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await api.get('/admin-users');
      setUsers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error('Kullanıcılar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setEditing(null);
    setForm({ ...emptyForm });
    setShowModal(true);
  };

  const openEdit = (user) => {
    setEditing(user);
    setForm({
      username: user.username || '',
      email: user.email || '',
      password: '',
      role: user.role || 'editor',
      is_active: user.is_active !== undefined ? user.is_active : true,
    });
    setShowModal(true);
  };

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        const payload = {
          username: form.username,
          email: form.email,
          role: form.role,
          is_active: form.is_active,
        };
        if (form.password) {
          payload.newPassword = form.password;
        }
        await api.put(`/admin-users/${editing.id}`, payload);
        toast.success('Kullanıcı güncellendi');
      } else {
        if (!form.password) {
          toast.error('Yeni kullanıcı için şifre zorunludur');
          setSaving(false);
          return;
        }
        await api.post('/admin-users', {
          username: form.username,
          email: form.email,
          password: form.password,
          role: form.role,
          is_active: form.is_active,
        });
        toast.success('Kullanıcı eklendi');
      }
      setShowModal(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'İşlem sırasında hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`"${user.username}" kullanicisini silmek istediginize emin misiniz?`)) return;
    try {
      await api.delete(`/admin-users/${user.id}`);
      toast.success('Kullanıcı silindi');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Silme sırasında hata oluştu');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isSelf = (user) => currentUser && currentUser.id === user.id;

  if (loading) {
    return <div className="admin-loading">Yükleniyor...</div>;
  }

  return (
    <div>
      <div className="admin-card">
        <div className="admin-card-header">
          <h2>Kullanıcı Yönetimi</h2>
          <button className="btn btn-primary" onClick={openAdd}>
            <FaPlus /> Yeni Kullanıcı
          </button>
        </div>

        <table className="admin-table">
          <thead>
            <tr>
              <th>Kullanıcı Adı</th>
              <th>E-posta</th>
              <th>Rol</th>
              <th>Durum</th>
              <th>Son Giriş</th>
              <th>İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ textAlign: 'center' }}>
                  Henuz kullanici yok
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <tr key={user.id}>
                  <td>
                    {user.username}
                    {isSelf(user) && <span style={{ marginLeft: 8, fontSize: '0.75rem', color: '#6b7280' }}>(siz)</span>}
                  </td>
                  <td>{user.email || '-'}</td>
                  <td>
                    <span className={`badge ${user.role === 'admin' ? 'badge-success' : 'badge-warning'}`}>
                      {user.role === 'admin' ? 'Admin' : 'Editor'}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${user.is_active ? 'badge-success' : 'badge-danger'}`}>
                      {user.is_active ? 'Aktif' : 'Pasif'}
                    </span>
                  </td>
                  <td>{formatDate(user.last_login)}</td>
                  <td>
                    <div className="actions">
                      <button className="btn btn-primary btn-sm" onClick={() => openEdit(user)}>
                        <FaEdit />
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDelete(user)}
                        disabled={isSelf(user)}
                        style={isSelf(user) ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
                        title={isSelf(user) ? 'Kendi hesabinizi silemezsiniz' : 'Sil'}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="admin-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>{editing ? 'Kullanıcı Düzenle' : 'Yeni Kullanıcı'}</h2>
              <button className="admin-modal-close" onClick={() => setShowModal(false)}>
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="admin-form">
              <div className="form-group">
                <label>Kullanıcı Adı *</label>
                <input
                  type="text"
                  value={form.username}
                  onChange={(e) => handleChange('username', e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label>E-posta</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>{editing ? 'Yeni Şifre (boş bırakırsanız değişmez)' : 'Şifre *'}</label>
                <input
                  type="password"
                  value={form.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  required={!editing}
                  minLength={4}
                />
              </div>
              <div className="form-group">
                <label>Rol</label>
                <select
                  value={form.role}
                  onChange={(e) => handleChange('role', e.target.value)}
                >
                  <option value="admin">Admin</option>
                  <option value="editor">Editor</option>
                </select>
              </div>
              <div className="form-group">
                <label>Durum</label>
                <div className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => handleChange('is_active', e.target.checked)}
                  />
                  <span>{form.is_active ? 'Aktif' : 'Pasif'}</span>
                </div>
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  İptal
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Kaydediliyor...' : 'Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
